import { useEffect, useRef, useState } from 'react';

interface UseRotatingPlaceholderOptions {
  /** Milliseconds each phrase stays visible before rotating. Default 3200ms — slow enough to read comfortably. */
  intervalMs?: number;
  /** When false, rotation is disabled and the first phrase is shown statically. */
  enabled?: boolean;
}

/**
 * Cycles through a list of placeholder phrases on a slow interval so an input
 * can show a gentle "marquee" of hints (e.g. "Ask Strive anything…",
 * "Type / for skills").
 *
 * Rotation is suppressed — and the first phrase shown statically — when the
 * user prefers reduced motion, when `enabled` is false, or when there is only
 * one phrase. `window.matchMedia` is guarded so the hook is safe in
 * non-browser / jsdom test environments.
 */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function readReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(REDUCED_MOTION_QUERY).matches
    : false;
}

/**
 * Cycles an index over `[0, length)` on a slow interval — the timer core of
 * `useRotatingPlaceholder`, exposed for consumers that rotate richer items
 * than strings (e.g. the onboarding-setup "Unlocking power" cards).
 * Rotation stops (index pinned to 0) when the user prefers reduced motion,
 * when `enabled` is false, or when there are fewer than two items.
 */
export function useRotatingIndex(
  length: number,
  { intervalMs = 3200, enabled = true }: UseRotatingPlaceholderOptions = {},
): number {
  const [index, setIndex] = useState(0);

  // Track the live reduced-motion preference so toggling it mid-session
  // starts/stops rotation, rather than freezing the mount-time value.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(readReducedMotion);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setPrefersReducedMotion(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  const shouldRotate = enabled && !prefersReducedMotion && length > 1;

  // Track the latest length so the interval callback wraps correctly without
  // re-subscribing every time the length changes.
  const lengthRef = useRef(length);
  lengthRef.current = length;

  useEffect(() => {
    if (!shouldRotate) {
      setIndex(0);
      return;
    }
    const id = window.setInterval(() => {
      // The interval only runs when length > 1 (see shouldRotate), so the
      // modulo divisor is always >= 2.
      setIndex((i) => (i + 1) % lengthRef.current);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [shouldRotate, intervalMs]);

  // Clamp in case the list shrank between renders.
  return index % Math.max(length, 1);
}

export function useRotatingPlaceholder(
  phrases: string[],
  options: UseRotatingPlaceholderOptions = {},
): string {
  const index = useRotatingIndex(phrases.length, options);
  return phrases[index] ?? phrases[0] ?? '';
}
